import { ChecklistLead, LeadStatus } from '@/types/leads.types';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowUpDown, ArrowUp, ArrowDown, FileText } from 'lucide-react';
import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useUpdateLead } from '@/hooks/useUpdateLead';

interface LeadsTableProps {
  leads: ChecklistLead[];
}

type SortColumn = 'name' | 'email' | 'practice_type' | 'created_at';
type SortDirection = 'asc' | 'desc';

const practiceTypeLabels: Record<string, string> = {
  hausarzt: 'Hausarzt',
  zahnarzt: 'Zahnarzt',
  facharzt: 'Facharzt',
  mvz: 'MVZ',
  klinik: 'Klinik',
};

const statusConfig: Record<LeadStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; color: string }> = {
  new: { label: 'Neu', variant: 'default', color: 'bg-blue-500' },
  contacted: { label: 'Kontaktiert', variant: 'secondary', color: 'bg-yellow-500' },
  qualified: { label: 'Qualifiziert', variant: 'default', color: 'bg-green-500' },
  converted: { label: 'Konvertiert', variant: 'default', color: 'bg-emerald-600' },
  lost: { label: 'Verloren', variant: 'outline', color: 'bg-gray-400' },
};

export const LeadsTable = ({ leads }: LeadsTableProps) => {
  const [sortColumn, setSortColumn] = useState<SortColumn>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [page, setPage] = useState(1);
  const [editingNotes, setEditingNotes] = useState<{ id: string; notes: string } | null>(null);
  const itemsPerPage = 20;
  const { updateLeadStatus, updateLeadNotes } = useUpdateLead();

  const handleStatusChange = async (lead: ChecklistLead, newStatus: LeadStatus) => {
    const success = await updateLeadStatus(lead.id, newStatus, {
      name: lead.name,
      email: lead.email,
      practice_type: lead.practice_type,
      status: lead.status,
    });
    if (success) {
      // Trigger a re-fetch by updating the leads array
      window.location.reload();
    }
  };

  const handleNotesSubmit = async () => {
    if (!editingNotes) return;
    const success = await updateLeadNotes(editingNotes.id, editingNotes.notes);
    if (success) {
      setEditingNotes(null);
      window.location.reload();
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedLeads = useMemo(() => {
    return [...leads].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (sortColumn === 'created_at') {
        aVal = new Date(aVal as string).getTime().toString();
        bVal = new Date(bVal as string).getTime().toString();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [leads, sortColumn, sortDirection]);

  const paginatedLeads = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return sortedLeads.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedLeads, page]);

  const totalPages = Math.ceil(sortedLeads.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, sortedLeads.length);

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) return <ArrowUpDown className="ml-2 h-4 w-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="ml-2 h-4 w-4" /> : <ArrowDown className="ml-2 h-4 w-4" />;
  };

  if (leads.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Keine Leads gefunden. Passen Sie die Filter an oder warten Sie auf neue Downloads.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('name')} className="hover:bg-transparent">
                  Name
                  <SortIcon column="name" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('email')} className="hover:bg-transparent">
                  E-Mail
                  <SortIcon column="email" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('practice_type')} className="hover:bg-transparent">
                  Praxistyp
                  <SortIcon column="practice_type" />
                </Button>
              </TableHead>
              <TableHead>Telefon</TableHead>
              <TableHead>Beratung</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Notizen</TableHead>
              <TableHead>
                <Button variant="ghost" onClick={() => handleSort('created_at')} className="hover:bg-transparent">
                  Download-Datum
                  <SortIcon column="created_at" />
                </Button>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedLeads.map((lead) => (
              <TableRow key={lead.id}>
                <TableCell className="font-medium">{lead.name}</TableCell>
                <TableCell>{lead.email}</TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {practiceTypeLabels[lead.practice_type] || lead.practice_type}
                  </Badge>
                </TableCell>
                <TableCell>{lead.phone || '-'}</TableCell>
                <TableCell>
                  {lead.wants_consultation ? (
                    <Badge>Ja</Badge>
                  ) : (
                    <Badge variant="secondary">Nein</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Select
                    value={lead.status}
                    onValueChange={(value) => handleStatusChange(lead, value as LeadStatus)}
                  >
                    <SelectTrigger className="w-[140px] h-8">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${statusConfig[lead.status].color}`} />
                        <SelectValue />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">🔵 Neu</SelectItem>
                      <SelectItem value="contacted">🟡 Kontaktiert</SelectItem>
                      <SelectItem value="qualified">🟢 Qualifiziert</SelectItem>
                      <SelectItem value="converted">✅ Konvertiert</SelectItem>
                      <SelectItem value="lost">⚪ Verloren</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8"
                        onClick={() => setEditingNotes({ id: lead.id, notes: lead.notes || '' })}
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        {lead.notes ? '📝' : '➕'}
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Notizen für {lead.name}</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Textarea
                          value={editingNotes?.id === lead.id ? editingNotes.notes : lead.notes || ''}
                          onChange={(e) => setEditingNotes({ id: lead.id, notes: e.target.value })}
                          placeholder="Notizen eingeben..."
                          rows={6}
                        />
                        <Button onClick={handleNotesSubmit} className="w-full">
                          Speichern
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </TableCell>
                <TableCell>
                  {format(new Date(lead.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Zeige {startIndex + 1} bis {endIndex} von {sortedLeads.length} Einträgen
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            Zurück
          </Button>
          <div className="flex items-center gap-2 px-3">
            <span className="text-sm text-muted-foreground">
              Seite {page} von {totalPages}
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
          >
            Weiter
          </Button>
        </div>
      </div>
    </div>
  );
};
